import React from 'react';
import { IGlobalStorefrontSettings, IStorefrontLink, IStorefrontSettings, IStorefrontTheme } from '../../../models/Storefront';
import './StorefrontEditor.css';
import './StorefrontSearchPageEditor.css';
import StorefrontHeaderEditor from './Header/StorefrontHeaderEditor';
import StorefrontFooterEditor from './Footer/StorefrontFooterEditor';
import { ActionButton, Callout, ChoiceGroup, CommandBar, DirectionalHint, IButtonStyles, ICommandBarItemProps, Icon, IChoiceGroupOption, IconButton, Panel, PanelType, Stack, TextField, Toggle, useTheme } from '@fluentui/react';
import StorefrontSearchBarEditor from './StorefrontSearchBarEditor';
import StorefrontAutocompleteStylePanel from './StorefrontAutocompleteStylePanel';
import { isDarkColor } from './colorUtils';
import {
    ISearchCardStyle,
    FacetSidebarLayout,
    IFacetSidebarConfig,
    ISearchContainerStyle,
    ISearchElementStyle,
    ISearchPageConfig,
    ISearchPriceStyle,
    ISearchTextStyle,
    parseSearchPageConfig,
    serializeSearchPageConfig,
    SearchResultViewMode,
} from './searchPageConfig';
import { lintSearchPageConfig } from './searchPageConfigLint';
import {
    cardStyleFrom,
    containerStyleFrom,
    cssText,
    EditPencil,
    elementStyleFrom,
    ElementPropertyRows,
    OPTION_ITEM_STYLES,
    parseNum,
    priceStyleFrom,
    renderOptionWithDescription,
    StyleGroupHeader,
    StyleOverridesGroup,
    StylePartAccordions,
    StyleRefPicker,
    textStyleFrom,
    useStyleRefPickerVisible,
} from './styleControls';
import { RowGroupLabel, StyleRow, StyleRowsWidthProvider, useCellControlStyles } from './styleRows';
import { stylePartForConfigKey, styleTargetForConfigKey } from './styleBulkApply';
import { countLocalOverrides, getStyleRef, resolveHoverPart, STYLE_PART_ICONS, STYLE_REF_KEY } from './stylesConfig';
import { resolvedStyleValue } from './styleConfig';
import ProductCardStylePanel from './ProductCardStylePanel';
import RawJsonEditor from './RawJsonEditor';
import PreviewCanvas from './PreviewCanvas';
import Setting from '../../Common/Setting';
import { useUrlPanelState } from '../../../hooks/useUrlState';

interface IStorefrontSearchPageEditorProps {
    globalSettings: IGlobalStorefrontSettings;
    globalTheme?: IStorefrontTheme;
    storefrontSettings: IStorefrontSettings;
    storefrontTheme?: IStorefrontTheme;
    editMode?: boolean;
    onStorefrontSettingsChange: (newSettings: IStorefrontSettings) => void;
    storefrontHeaderLinks: IStorefrontLink[];
    globalHeaderLinks: IStorefrontLink[];
    onHeaderLinksChange: (newLinks: IStorefrontLink[]) => void;
    storefrontFooterLinks: IStorefrontLink[];
    globalFooterLinks: IStorefrontLink[];
    onFooterLinksChange: (newLinks: IStorefrontLink[]) => void;
}

interface ISampleProduct {
    name: string;
    price: number;
    manufacturer?: string;
    category?: string;
    subCategory?: string;
}

// Sample data used to render a representative preview of the search results.
const SAMPLE_PRODUCTS: ISampleProduct[] = [
    { name: 'Workstation Lenovo Laptop', price: 2497.5, manufacturer: 'Lenovo', category: 'Computers', subCategory: 'Laptops' },
    { name: 'Standard Lenovo Laptop', price: 2186.18, manufacturer: 'Lenovo', category: 'Computers', subCategory: 'Laptops' },
    { name: 'Dell 14 Latitude 7420 Laptop (Carbon Fiber)', price: 1078.8, manufacturer: 'Dell', category: 'Computers', subCategory: 'Laptops' },
];

const formatCurrency = (value: number) =>
    value.toLocaleString(undefined, { style: 'currency', currency: 'USD' });

const FACET_LAYOUT_OPTIONS: IChoiceGroupOption[] = [
    { key: 'panel', text: 'Side panel' },
    { key: 'inline', text: 'Inline menus' },
];

// Preview style builders + the shared element control surface come from styleControls.tsx —
// this file used to carry byte-identical private copies. Sharing them means Storefront Styles
// ($styleRef) resolution, which lives inside those builders, applies to the Search preview too.
// The hover-revealed EditPencil is shared from there as well (it was a private copy here first).

// The styleable elements, each with its own right-side panel. 'card' is the whole product
// card/row — tile, name, price, image and hover together (ProductCardStylePanel); the image
// used to have a panel of its own. 'filters' and 'searchBar' were raw-JSON-only until the
// command bar gave them buttons.
type PanelKey = 'card' | 'container' | 'paginator' | 'breadcrumb' | 'metaChips' | 'filters' | 'searchBar' | 'suggestions';

// ISearchContainerStyle / ISearchTextStyle / ISearchCardStyle are narrower than the
// ISearchElementStyle the shared rows speak, and they disagree on a couple of named props, so the
// dicts don't structurally interchange in EITHER direction, even though at RUNTIME they're all
// open camelCase CSS bags with an index signature. One loose cast at the boundary (value in, patch
// out), exactly as ProductCardStylePanel does it.
const loose = (dict?: { [prop: string]: any }): any => dict;

// The Options menu's two-line toggle renderer (renderOptionWithDescription / OPTION_ITEM_STYLES)
// started life here and moved into styleControls.tsx once the Cart page editor grew its own
// Options menu.

// The multi-part panels below (Paginator, Breadcrumbs, Row Meta, Filters) present their parts as
// one-open-at-a-time accordions via the shared StylePartAccordions, which started life here and
// moved into styleControls.tsx once the Product page editor needed the identical surface.

interface IPreviewStyles {
    card: React.CSSProperties;
    name: React.CSSProperties;
    price: React.CSSProperties;
    image: React.CSSProperties;
    container: React.CSSProperties;
    paginatorContainer: React.CSSProperties;
    paginatorCell: React.CSSProperties;
    breadcrumbContainer: React.CSSProperties;
    breadcrumbItem: React.CSSProperties;
    searchBar: React.CSSProperties;
    facetPanel: React.CSSProperties;
    addToCartButton: React.CSSProperties;
    addToCartLabel: React.CSSProperties;
    categoryChip: React.CSSProperties;
    manufacturerChip: React.CSSProperties;
}

// The quick style popover behind a result row/card in the preview: the Search page's counterpart
// to the home sections' SectionCardStylePopover.
//
// Search has no section layer: every result on the page is painted by the ONE page-level `card`
// group, so there's no "this section vs page-wide" split to explain and no second style layer to
// offer. What's left is the 95% case (switch the named style), plus a way out to the full panel.
//
// Deliberately small: picking a style repaints the preview instantly (the config update flows
// straight back through onStorefrontSettingsChange). Anything more (raw CSS overrides) hands off
// to ProductCardStylePanel rather than growing a second editing surface here.
const SearchCardStylePopover: React.FC<{
    anchor: HTMLElement;
    /** 'Row' in rows view, 'Card' in cards view. Matches whatever the results are drawn as. */
    modeNoun: 'Card' | 'Row';
    card?: ISearchCardStyle;
    // Deliberately loose, like StyleRefPicker's own onChange: all the picker ever writes is $styleRef.
    onCardChange: (patch: { [prop: string]: any }) => void;
    /** Drop the local overrides but keep the $styleRef (revert to the style as designed). */
    onClearOverrides: () => void;
    /** Open the full card/row style panel. */
    onOpenPanel: () => void;
    onDismiss: () => void;
}> = ({ anchor, modeNoun, card, onCardChange, onClearOverrides, onOpenPanel, onDismiss }) => {
    const theme = useTheme();
    const target = styleTargetForConfigKey('searchPageConfig', 'card');
    // The picker renders nothing when the org has no card styles or the user can't administer them.
    // Ask first, so the popover can say why it's empty instead of just being empty.
    const pickerVisible = useStyleRefPickerVisible(target);
    const overrideCount = countLocalOverrides(card);
    const actionStyles: IButtonStyles = { root: { height: 30, padding: 0 }, label: { fontSize: 12, fontWeight: 400 } };
    const noun = modeNoun.toLowerCase();

    return (
        <Callout
            target={anchor}
            onDismiss={onDismiss}
            setInitialFocus
            gapSpace={8}
            directionalHint={DirectionalHint.bottomCenter}
        >
            <div style={{ padding: '0.75em 1em', width: 260, boxSizing: 'border-box' }}>
                {/* Scope line: the same product card shape appears on the Home page from a different
                    config blob, so the popover states which page's results it edits. */}
                <div style={{ fontSize: 13, fontWeight: 600 }}>Product {modeNoun}s</div>
                <div style={{ fontSize: 11, color: theme.palette.neutralSecondary, marginBottom: '0.5em' }}>Search results</div>
                {pickerVisible
                    ? (
                        // Narrower control column than the panels, since this is a 260px popover.
                        <StyleRowsWidthProvider width={130}>
                            <StyleRefPicker rowLayout value={card} onChange={onCardChange} target={target} />
                        </StyleRowsWidthProvider>
                    )
                    : <div style={{ fontSize: 12, color: theme.palette.neutralSecondary }}>No saved card styles yet.</div>}
                {/* Only when something IS overridden: with no overrides this row would just be a
                    second button to the same panel the footer action already opens. With overrides
                    it earns its place: it states the count and carries the one-click revert. */}
                {overrideCount > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <ActionButton
                            iconProps={{ iconName: 'Settings' }}
                            text={`Advanced overrides (${overrideCount})`}
                            title={`Open the full ${noun} style panel, where these ${overrideCount} local override${overrideCount === 1 ? '' : 's'} live.`}
                            onClick={() => { onDismiss(); onOpenPanel(); }}
                            styles={actionStyles}
                        />
                        {/* Quick "back to the style as designed": drop the overrides but keep the
                            $styleRef (same semantics as StyleOverridesGroup's Clear), without
                            leaving the popover. */}
                        <IconButton
                            iconProps={{ iconName: 'ClearFormatting' }}
                            title={`Clear the ${overrideCount} override${overrideCount === 1 ? '' : 's'}, reverting to the style as designed`}
                            ariaLabel='Clear overrides'
                            onClick={onClearOverrides}
                            styles={{ root: { height: 30, width: 28 }, icon: { fontSize: 13 } }}
                        />
                    </div>
                )}
                <div style={{ borderTop: `1px solid ${theme.palette.neutralLight}`, marginTop: '0.25em' }} />
                <ActionButton
                    iconProps={{ iconName: 'GridViewMedium' }}
                    text={`Full ${noun} style panel`}
                    title={`Edit the tile, name, price, image and hover styling of every result ${noun}.`}
                    onClick={() => { onDismiss(); onOpenPanel(); }}
                    styles={actionStyles}
                />
            </div>
        </Callout>
    );
};

// Rows use the labeled button; cards use the compact icon-only button, matching
// the storefront's section product cards.
const CartButtonPreview: React.FC<{ compact?: boolean; style?: React.CSSProperties; labelStyle?: React.CSSProperties }> = ({ compact, style, labelStyle }) =>
    compact ? (
        <div className='sf-search-cart-icon-only' title='Add to cart' style={style}><Icon iconName='ShoppingCart' /></div>
    ) : (
        <div className='sf-search-result-cart' style={style}>
            <span className='sf-search-result-cart-icon'><Icon iconName='ShoppingCart' /></span>
            <span className='sf-search-result-cart-label' style={labelStyle}>Add to Cart</span>
        </div>
    );

const FilterBreadcrumbPreview: React.FC<{ containerStyle?: React.CSSProperties; itemStyle?: React.CSSProperties; onEdit?: () => void }> = ({ containerStyle, itemStyle, onEdit }) => (
    // Whole-element click opens the breadcrumbs panel in edit mode; the pencil is the visible
    // affordance for it (same deal on the paginator, meta line and filter surfaces below).
    <div
        className={`sf-search-filters sf-pencil-host${onEdit ? ' sf-editable' : ''}`}
        style={containerStyle}
        title={onEdit ? 'Edit the filter breadcrumbs' : undefined}
        onClick={onEdit}
    >
        {onEdit && <EditPencil onClick={onEdit} title='Edit breadcrumbs' />}
        <div className='sf-search-chip' style={itemStyle}><b>Category:</b> Computers &times;</div>
        <span className='sf-search-filters-sep'>&gt;</span>
        <div className='sf-search-chip' style={itemStyle}><b>Sub-Category:</b> Laptops &times;</div>
    </div>
);

// Mirrors the live storefront Paginator (ac-storefront-web Components/Paginator.tsx): a compact
// inline control — « prev, a dropdown showing the "start–end of total" range, » next — that sits
// beside the search bar rather than on its own numbered row.
const PaginatorPreview: React.FC<{ containerStyle?: React.CSSProperties; cellStyle?: React.CSSProperties; onEdit?: () => void }> = ({ containerStyle, cellStyle, onEdit }) => (
    <div
        className={`sf-search-paginator sf-pencil-host${onEdit ? ' sf-editable' : ''}`}
        title={onEdit ? 'Edit the paginator' : undefined}
        onClick={onEdit}
    >
        {onEdit && <EditPencil onClick={onEdit} title='Edit paginator' />}
        <div className='sf-search-paginator-menu' style={containerStyle}>
            <div className='sf-search-paginator-item' style={cellStyle} title='Previous page'>&laquo;</div>
            <div className='sf-search-paginator-item' style={{ gap: 6, ...cellStyle }} title='Go to page'>
                1–25 of 37
                <Icon iconName='ChevronDown' styles={{ root: { fontSize: 12 } }} />
            </div>
            <div className='sf-search-paginator-item' style={cellStyle} title='Next page'>&raquo;</div>
        </div>
    </div>
);

const SearchResultRowPreview: React.FC<{
    product: ISampleProduct;
    styles: IPreviewStyles;
    /** Opens the quick style popover anchored on this exact row (edit mode only). */
    onQuickStyle?: (anchor: HTMLElement) => void;
    onEditMeta?: () => void;
}> = ({ product, styles, onQuickStyle, onEditMeta }) => {
    const crumbs: string[] = [];
    if (product.category) crumbs.push(product.category);
    if (product.subCategory) crumbs.push(product.subCategory);
    const manufacturerChip = Object.keys(styles.manufacturerChip).length ? { display: 'inline-block', ...styles.manufacturerChip } : undefined;
    const categoryChip = Object.keys(styles.categoryChip).length ? { display: 'inline-block', ...styles.categoryChip } : undefined;
    // The popover anchors on THIS row's element, so it opens beside whichever row was clicked
    // (a ref rather than the click's currentTarget, so the pencil can anchor on it too).
    const rootRef = React.useRef<HTMLDivElement>(null);
    const openQuickStyle = () => { if (rootRef.current) onQuickStyle?.(rootRef.current); };
    return (
        <div
            ref={rootRef}
            className={`sf-search-result sf-pencil-host${onQuickStyle ? ' sf-editable' : ''}`}
            style={styles.card}
            title={onQuickStyle ? 'Edit the row style' : undefined}
            onClick={onQuickStyle ? openQuickStyle : undefined}
        >
            {onQuickStyle && <EditPencil onClick={openQuickStyle} title='Edit row style' />}
            <div className='sf-search-result-image' style={Object.keys(styles.image).length ? { ...styles.image, overflow: 'hidden' } : undefined}>
                <Icon iconName='Photo2' />
            </div>
            <div className='sf-search-result-body'>
                <div className='sf-search-result-name' style={styles.name}>{product.name}</div>
                <div className='sf-search-result-actions'>
                    <div className='sf-search-result-price' style={styles.price}>{formatCurrency(product.price)}</div>
                    <CartButtonPreview style={styles.addToCartButton} labelStyle={styles.addToCartLabel} />
                </div>
                {/* The meta line sits INSIDE the row, so its click has to stop before it reaches the
                    row's own quick-style popover, since the two edit different config keys. */}
                <div
                    className={`sf-search-result-meta sf-pencil-host${onEditMeta ? ' sf-editable' : ''}`}
                    title={onEditMeta ? 'Edit the row meta chips' : undefined}
                    onClick={onEditMeta ? (e) => { e.stopPropagation(); onEditMeta(); } : undefined}
                >
                    {onEditMeta && <EditPencil onClick={onEditMeta} title='Edit meta chips' />}
                    {product.manufacturer && (
                        <span style={manufacturerChip}>Manufacturer: <span className='sf-search-result-link'>{product.manufacturer}</span></span>
                    )}
                    {crumbs.length > 0 && (
                        <span style={categoryChip}>
                            Category:{' '}
                            {crumbs.map((c, i) => (
                                <React.Fragment key={c}>
                                    {i > 0 && <span style={{ margin: '0 4px' }}>&gt;</span>}
                                    <span className='sf-search-result-link'>{c}</span>
                                </React.Fragment>
                            ))}
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
};

const SearchResultCardPreview: React.FC<{
    product: ISampleProduct;
    styles: IPreviewStyles;
    onQuickStyle?: (anchor: HTMLElement) => void;
}> = ({ product, styles, onQuickStyle }) => {
    const rootRef = React.useRef<HTMLDivElement>(null);
    const openQuickStyle = () => { if (rootRef.current) onQuickStyle?.(rootRef.current); };
    return (
        <div
            ref={rootRef}
            className={`sf-search-card sf-pencil-host${onQuickStyle ? ' sf-editable' : ''}`}
            style={styles.card}
            title={onQuickStyle ? 'Edit the card style' : undefined}
            onClick={onQuickStyle ? openQuickStyle : undefined}
        >
            {onQuickStyle && <EditPencil onClick={openQuickStyle} title='Edit card style' />}
            {/* Visual parity with the storefront's top-edge filter shortcuts (manufacturer
                top-left, category top-right). Non-functional here — the preview is static. */}
            {product.manufacturer && (
                <div className='sf-search-card-filter' style={{ left: 8 }} title={`Filter by manufacturer: ${product.manufacturer}`}>
                    <Icon iconName='Manufacturing' />
                </div>
            )}
            {(product.subCategory || product.category) && (
                <div className='sf-search-card-filter' style={{ right: 8 }} title={`Filter by category: ${product.subCategory || product.category}`}>
                    <Icon iconName='Tag' />
                </div>
            )}
            <div className='sf-search-card-image' style={Object.keys(styles.image).length ? { ...styles.image, overflow: 'hidden' } : undefined}>
                <Icon iconName='Photo2' />
            </div>
            <div className='sf-search-card-name' style={styles.name}>{product.name}</div>
            <div className='sf-search-card-price' style={styles.price}>{formatCurrency(product.price)}</div>
            <div className='sf-search-card-cart'>
                <CartButtonPreview compact style={styles.addToCartButton} />
            </div>
        </div>
    );
};

// Sample facet groups for the filter-panel representation in the preview.
const SAMPLE_FACETS: { label: string; options: { name: string; count: number; checked?: boolean }[] }[] = [
    { label: 'Manufacturer', options: [{ name: 'Lenovo', count: 2, checked: true }, { name: 'Dell', count: 1 }] },
    { label: 'Form Factor', options: [{ name: 'Laptop', count: 3 }, { name: 'Desktop', count: 0 }] },
];

const FilterSelect: React.FC<{ label: string; value: string; className?: string }> = ({ label, value, className }) => (
    <div className={`sf-filter-select${className ? ` ${className}` : ''}`}>
        <span className='sf-filter-select-text'>{label ? <><b>{label}:</b> {value}</> : value}</span>
        <Icon iconName='ChevronDown' />
    </div>
);

// Left-hand facet filter panel, shown when facet filters are enabled with the "Side panel" layout.
// Both the panel and its headings are styleable (facetPanel / facetHeading), so the whole aside
// is click-to-edit onto the Filters panel.
const FilterPanelPreview: React.FC<{
    priceFilter?: boolean;
    style?: React.CSSProperties;
    headingStyle?: React.CSSProperties;
    onEdit?: () => void;
}> = ({ priceFilter, style, headingStyle, onEdit }) => (
    <aside
        className={`sf-filter-panel sf-pencil-host${onEdit ? ' sf-editable' : ''}`}
        style={style}
        title={onEdit ? 'Edit the filters' : undefined}
        onClick={onEdit}
    >
        {onEdit && <EditPencil onClick={onEdit} title='Edit filters' />}
        <div className='sf-filter-panel-header'>
            {/* Fluent subtle-style button with a left X icon; color inherits the panel's
                foreground so it goes light on dark storefronts (like the real storefront). */}
            <ActionButton
                iconProps={{ iconName: 'Cancel' }}
                className='sf-filter-clear-btn'
                styles={{
                    root: { height: 'auto', padding: '2px 4px', color: 'inherit' },
                    icon: { color: 'inherit', fontSize: 12, margin: '0 4px 0 0' },
                    label: { fontWeight: 400, fontSize: 12, margin: 0 },
                }}
            >
                Clear filters
            </ActionButton>
        </div>
        <div className='sf-filter-field'><label>Segment</label><FilterSelect label='' value='All' /></div>
        <div className='sf-filter-field'><label>Category</label><FilterSelect label='' value='Computers' /></div>
        <div className='sf-filter-field'><label>Sub-Category</label><FilterSelect label='' value='Laptops' /></div>
        {SAMPLE_FACETS.map((f) => (
            <div key={f.label} className='sf-filter-facet'>
                <div className='sf-filter-facet-label' style={headingStyle}>{f.label}</div>
                {f.options.map((o) => (
                    <div key={o.name} className='sf-filter-check'>
                        <span className={`sf-filter-checkbox${o.checked ? ' sf-filter-checkbox-on' : ''}`}>{o.checked && <Icon iconName='CheckMark' />}</span>
                        <span className='sf-filter-check-name'>{o.name} ({o.count})</span>
                    </div>
                ))}
            </div>
        ))}
        {priceFilter && (
            <div className='sf-filter-facet'>
                <div className='sf-filter-facet-label' style={headingStyle}>Price Range</div>
                <div className='sf-filter-price'>
                    <span className='sf-filter-price-input'>$ Min</span>
                    <span>–</span>
                    <span className='sf-filter-price-input'>$ Max</span>
                </div>
            </div>
        )}
    </aside>
);

// Inline filter menus, shown when facet filters are enabled with the "Inline menus" layout.
const InlineFiltersPreview: React.FC<{ priceFilter?: boolean; onEdit?: () => void }> = ({ priceFilter, onEdit }) => (
    <div
        className={`sf-inline-filters sf-pencil-host${onEdit ? ' sf-editable' : ''}`}
        title={onEdit ? 'Edit the filters' : undefined}
        onClick={onEdit}
    >
        {onEdit && <EditPencil onClick={onEdit} title='Edit filters' />}
        <FilterSelect label='Segment' value='All' className='sf-inline-filter' />
        <FilterSelect label='Category' value='Computers' className='sf-inline-filter' />
        <FilterSelect label='Sub-Category' value='Laptops' className='sf-inline-filter' />
        <FilterSelect label='' value='Manufacturer' className='sf-inline-filter' />
        {priceFilter && <FilterSelect label='' value='Price' className='sf-inline-filter' />}
    </div>
);

const StorefrontSearchPageEditor: React.FC<IStorefrontSearchPageEditorProps> = (props) => {
    const backgroundColor = resolvedStyleValue(props.storefrontSettings, 'backgroundColor') || props.storefrontTheme?.backgroundColor || props.globalSettings.backgroundColor || props.globalTheme?.backgroundColor || '#fff';
    const textColor = resolvedStyleValue(props.storefrontSettings, 'textColor') || props.storefrontTheme?.textColor || props.globalSettings.textColor || props.globalTheme?.textColor || '#000';
    const isDark = isDarkColor(backgroundColor);
    // The one non-row control on a flat surface (the container panel's Card gap) borrows the same
    // stripped interior every other flat row uses, so it lines up with the rows above it.
    const cell = useCellControlStyles();

    const config = parseSearchPageConfig(props.storefrontSettings.searchPageConfig);
    const viewMode: SearchResultViewMode = config.viewMode ?? 'rows';
    // The card/container style controls apply to whichever result item is shown, so
    // label them "Card"/"Row" to match the selected view.
    const modeNoun = viewMode === 'cards' ? 'Card' : 'Row';

    const updateConfig = (patch: Partial<ISearchPageConfig>) => {
        const next = { ...config, ...patch };
        props.onStorefrontSettingsChange({ ...props.storefrontSettings, searchPageConfig: serializeSearchPageConfig(next) });
    };
    const updateCard = (patch: Partial<ISearchCardStyle>) => updateConfig({ card: { ...config.card, ...patch } });
    const updateTitle = (patch: Partial<ISearchTextStyle>) => updateConfig({ title: { ...config.title, ...patch } });
    const updatePrice = (patch: Partial<ISearchPriceStyle>) => updateConfig({ price: { ...config.price, ...patch } });
    const updateImage = (patch: Partial<ISearchElementStyle>) => updateConfig({ image: { ...config.image, ...patch } });
    // cardHover: applied by the storefront as a scoped `:hover` rule on the tile. It has existed in
    // the config (and in bulk-apply's allow-list) since the styles work, but was reachable only from
    // Raw JSON until the merged card panel gave it a group.
    const updateCardHover = (patch: Partial<ISearchElementStyle>) => updateConfig({ cardHover: { ...config.cardHover, ...patch } });
    const updateContainer = (patch: Partial<ISearchContainerStyle>) => updateConfig({ container: { ...config.container, ...patch } });
    const updatePaginatorContainer = (patch: Partial<ISearchElementStyle>) => updateConfig({ paginatorContainer: { ...config.paginatorContainer, ...patch } });
    const updatePaginatorCell = (patch: Partial<ISearchElementStyle>) => updateConfig({ paginatorCell: { ...config.paginatorCell, ...patch } });
    const updateBreadcrumbContainer = (patch: Partial<ISearchElementStyle>) => updateConfig({ breadcrumbContainer: { ...config.breadcrumbContainer, ...patch } });
    const updateBreadcrumbItem = (patch: Partial<ISearchElementStyle>) => updateConfig({ breadcrumbItem: { ...config.breadcrumbItem, ...patch } });
    const updateCategoryChip = (patch: Partial<ISearchElementStyle>) => updateConfig({ categoryChip: { ...config.categoryChip, ...patch } });
    const updateManufacturerChip = (patch: Partial<ISearchElementStyle>) => updateConfig({ manufacturerChip: { ...config.manufacturerChip, ...patch } });
    const updateFacetSidebar = (patch: Partial<IFacetSidebarConfig>) => updateConfig({ facetSidebar: { ...config.facetSidebar, ...patch } });
    // facetPanel / facetHeading / searchBar were config keys the storefront already rendered but no
    // control ever wrote (raw JSON was the only way in). The Filters and Search Bar panels below own
    // them now.
    const updateFacetPanel = (patch: Partial<ISearchElementStyle>) => updateConfig({ facetPanel: { ...config.facetPanel, ...patch } });
    const updateFacetHeading = (patch: Partial<ISearchTextStyle>) => updateConfig({ facetHeading: { ...config.facetHeading, ...patch } });
    const updateSearchBar = (patch: Partial<ISearchElementStyle>) => updateConfig({ searchBar: { ...config.searchBar, ...patch } });
    const facetsEnabled = config.facetSidebar?.enabled ?? false;
    const facetLayout: FacetSidebarLayout = config.facetSidebar?.layout ?? 'panel';
    const panelLayoutOn = facetsEnabled && facetLayout === 'panel';
    const inlineLayoutOn = facetsEnabled && facetLayout === 'inline';

    /*
     * Each styleable element opens in its own right-side Panel (like the home section editor). One
     * panel is open at a time; its command bar button is highlighted. Panels can be opened from the
     * bar, from the hover pencil on the previewed element, or from the element itself (click-to-edit).
     *
     * Each of the eight panels is its own URL-driven slot (`?panel=<key>`) — the same treatment the
     * home editor's toolbar panels got. This used to be plain useState, which meant a link to a field
     * inside any of them opened the page and then honestly reported the field wasn't there, because
     * nothing had opened the panel holding it. setOpenPanel/openPanel keep their original shape so
     * every existing call site below is unchanged.
     */
    const panelLinks: { [K in PanelKey]: ReturnType<typeof useUrlPanelState> } = {
        card: useUrlPanelState('card'),
        container: useUrlPanelState('container'),
        paginator: useUrlPanelState('paginator'),
        breadcrumb: useUrlPanelState('breadcrumb'),
        metaChips: useUrlPanelState('metaChips'),
        filters: useUrlPanelState('filters'),
        searchBar: useUrlPanelState('searchBar'),
        suggestions: useUrlPanelState('suggestions'),
    };
    const openPanel = (Object.keys(panelLinks) as PanelKey[]).find((k) => panelLinks[k].isOpen) ?? null;
    const setOpenPanel = (key: PanelKey | null) => {
        if (key) panelLinks[key].open();
        else if (openPanel) panelLinks[openPanel].close();
    };
    const closePanel = () => setOpenPanel(null);
    // The result row/card quick style popover, anchored on the exact element clicked. Owned here
    // (not in the preview items) because exactly ONE may be open across the whole page.
    const [cardPopover, setCardPopover] = React.useState<{ anchor: HTMLElement } | undefined>();

    // Raw JSON mode is RawJsonEditor's (shared with the Home page editor); this adapter hands it the
    // Search config's own linter, which checks every leaf against CSS.supports(), in place of the
    // generic JSON/shape check. Warnings (e.g. an unknown key) render amber and still apply.
    const lintAdapter = (raw: string) => {
        const result = lintSearchPageConfig(raw);
        return { valid: result.valid, errors: result.errors, warnings: result.warnings, parsed: result.parsed };
    };

    // Second argument = the Storefront Style part implied by the config key (stylePartForKey), so
    // an element carrying a $styleRef picks up the right part of the referenced style. Keys with no
    // mapping resolve against the referenced style's default part.
    const previewStyles: IPreviewStyles = {
        card: cardStyleFrom(config.card, stylePartForConfigKey('searchPageConfig', 'card')),
        name: textStyleFrom(config.title, stylePartForConfigKey('searchPageConfig', 'title')),
        price: priceStyleFrom(config.price, stylePartForConfigKey('searchPageConfig', 'price')),
        image: elementStyleFrom(config.image, stylePartForConfigKey('searchPageConfig', 'image')),
        container: containerStyleFrom(config.container),
        paginatorContainer: elementStyleFrom(config.paginatorContainer),
        paginatorCell: elementStyleFrom(config.paginatorCell),
        breadcrumbContainer: elementStyleFrom(config.breadcrumbContainer),
        breadcrumbItem: elementStyleFrom(config.breadcrumbItem),
        searchBar: elementStyleFrom(config.searchBar, stylePartForConfigKey('searchPageConfig', 'searchBar')),
        facetPanel: elementStyleFrom(config.facetPanel),
        addToCartButton: elementStyleFrom(config.addToCartButton, stylePartForConfigKey('searchPageConfig', 'addToCartButton')),
        addToCartLabel: elementStyleFrom(config.addToCartLabel, stylePartForConfigKey('searchPageConfig', 'addToCartLabel')),
        categoryChip: elementStyleFrom(config.categoryChip),
        manufacturerChip: elementStyleFrom(config.manufacturerChip),
    };
    // The facet group headings inside the panel ("Manufacturer", "Form Factor", "Price Range").
    const facetHeadingStyle = textStyleFrom(config.facetHeading);

    // Hover has to paint as a real :hover rule — the preview tiles carry previewStyles.card INLINE,
    // so a plain rule would lose; cssText appends !important (same trick as the Home editor's scoped
    // stylesheet). resolveHoverPart is the renderer's exact pipeline (SearchPage.tsx): one ref (the
    // hover dict's own, else the resting card's), that preset's `hover` part under the local dict.
    const hoverRuleCss = cssText(elementStyleFrom(resolveHoverPart(config.cardHover, config.card), 'hover'));

    // The Search Page Style bar. One button, one panel; `checked` renders the pressed highlight
    // while that entry's panel is open (mirrors the Home page editor's bar, which replaced the same
    // labeled-columns layout this file used to carry). The two settings that aren't a style surface
    // (sticky controls and the global image hover swap) sit in the far "Options" menu.
    // Every entry carries a `title` tooltip saying what it opens/does: the bar's short labels name
    // the element, but (unlike the labeled-columns layout this replaced) they don't describe it.
    const styleBarItems: ICommandBarItemProps[] = [
        {
            key: 'view',
            text: `View: ${viewMode === 'cards' ? 'Cards' : 'Rows'}`,
            title: 'How the results are laid out: full-width rows, or a grid of cards.',
            iconProps: { iconName: 'View' },
            subMenuProps: {
                items: [
                    { key: 'rows', text: 'Rows', title: 'One result per line, with the meta chips under each name.', canCheck: true, checked: viewMode !== 'cards', onClick: () => updateConfig({ viewMode: 'rows' }) },
                    { key: 'cards', text: 'Cards', title: 'A grid of product cards, like the home page sections.', canCheck: true, checked: viewMode === 'cards', onClick: () => updateConfig({ viewMode: 'cards' }) },
                ],
            },
        },
        // The tile, name, price, image and hover are parts of the same result item and are edited
        // together (ProductCardStylePanel).
        { key: 'card', text: `${modeNoun} Style`, title: `Each result ${modeNoun.toLowerCase()}: the tile, name, price, image and hover styling.`, iconProps: { iconName: 'GridViewMedium' }, checked: openPanel === 'card', onClick: () => setOpenPanel('card') },
        { key: 'container', text: 'Container', title: `The box around the whole results ${viewMode === 'cards' ? 'grid' : 'list'}, and the gap between results.`, iconProps: { iconName: 'Product' }, checked: openPanel === 'container', onClick: () => setOpenPanel('container') },
        ...(viewMode !== 'cards'
            ? [{ key: 'metaChips', text: 'Row Meta', title: 'The Category and Manufacturer chips on each result row.', iconProps: { iconName: 'Tag' }, checked: openPanel === 'metaChips', onClick: () => setOpenPanel('metaChips') }]
            : []),
        { key: 'paginator', text: 'Paginator', title: 'The page navigation control beside the search bar.', iconProps: { iconName: 'PageList' }, checked: openPanel === 'paginator', onClick: () => setOpenPanel('paginator') },
        // The panel/inline menus carry the category navigation, so the preview only draws
        // breadcrumbs when facet filters are off, so there is no button for something that isn't on screen.
        ...(!facetsEnabled
            ? [{ key: 'breadcrumb', text: 'Breadcrumbs', title: 'The selected-filter chips above the results, e.g. "Category: Computers".', iconProps: { iconName: 'Breadcrumb' }, checked: openPanel === 'breadcrumb', onClick: () => setOpenPanel('breadcrumb') }]
            : []),
        { key: 'filters', text: 'Filters', title: 'Shopper facet filtering: turn it on/off, pick its layout, and style the filter panel.', iconProps: { iconName: 'Filter' }, checked: openPanel === 'filters', onClick: () => setOpenPanel('filters') },
        { key: 'searchBar', text: 'Search Bar', title: 'The keyword box above the results.', iconProps: { iconName: 'Search' }, checked: openPanel === 'searchBar', onClick: () => setOpenPanel('searchBar') },
        { key: 'suggestions', text: 'Search Suggestions', title: 'Style the suggestion panel that drops below this page’s search bar.', iconProps: { iconName: 'Color' }, checked: openPanel === 'suggestions', onClick: () => setOpenPanel('suggestions') },
    ];

    // Far-side menu: the two page-level switches that style nothing. Image hover swap is a
    // STOREFRONT setting (it applies to product images everywhere), which is why it sits here
    // rather than in the card panel. It used to be a floating chip over the results that
    // overlapped the first row.
    const optionsBarItems: ICommandBarItemProps[] = [
        {
            key: 'options',
            text: 'Options',
            title: 'Page behavior settings: sticky scrolling and the storefront-wide image hover swap.',
            iconProps: { iconName: 'Settings' },
            subMenuProps: {
                items: [
                    {
                        key: 'stickyControls',
                        text: 'Sticky controls',
                        canCheck: true,
                        checked: config.stickyControls ?? false,
                        onClick: () => updateConfig({ stickyControls: !(config.stickyControls ?? false) }),
                        onRenderContent: renderOptionWithDescription('Keep the filter panel, search bar and paginator pinned while only the results scroll.'),
                        itemProps: { styles: OPTION_ITEM_STYLES },
                    },
                    {
                        key: 'imageHoverSwap',
                        text: 'Image hover swap',
                        canCheck: true,
                        checked: props.storefrontSettings.enableImageHoverSwap ?? false,
                        onClick: () => props.onStorefrontSettingsChange({ ...props.storefrontSettings, enableImageHoverSwap: !(props.storefrontSettings.enableImageHoverSwap ?? false) }),
                        onRenderContent: renderOptionWithDescription("Swap to the product's second image on hover. Applies to product images across the storefront."),
                        itemProps: { styles: OPTION_ITEM_STYLES },
                    },
                ],
            },
        },
    ];

    return (
        <div>
            {/* Scoped stylesheet for the tile hover styling (cards + rows) — see hoverRuleCss above.
                The transition line mirrors the storefront's (SearchPage.tsx) so the change animates. */}
            {hoverRuleCss && <style>{`
                .sf-search-cards .sf-search-card, .sf-search-results .sf-search-result { transition: box-shadow .18s ease, transform .18s ease, border-color .18s ease, background-color .18s ease; }
                .sf-search-cards .sf-search-card:hover, .sf-search-results .sf-search-result:hover { ${hoverRuleCss} }
            `}</style>}
            {props.editMode && (
                <div style={{ marginBottom: '1em' }}>
                <RawJsonEditor
                    label='Search Page Style'
                    liveObject={config}
                    onApply={(json) => props.onStorefrontSettingsChange({ ...props.storefrontSettings, searchPageConfig: json })}
                    lint={lintAdapter}
                    keysHint={`Keys: viewMode, cardGap, stickyControls (boolean), facetSidebar ({ enabled, layout: "panel"|"inline", priceFilter }), card, cardHover, title, price, image, container, paginatorContainer, paginatorCell, breadcrumbContainer, breadcrumbItem, searchBar, facetPanel, facetHeading, addToCartButton, addToCartLabel, categoryChip, manufacturerChip. Any CSS property (camelCase, e.g. boxShadow) works. Numbers become px — use a string for other units (e.g. "600" for font-weight). url(), @import, javascript: and expression() are blocked. Changes apply while the JSON is valid.`}
                >
                    <CommandBar
                        items={styleBarItems}
                        farItems={optionsBarItems}
                        styles={{ root: { padding: 0, border: '1px solid #edebe9', borderRadius: 4, background: '#faf9f8' } }}
                    />
                </RawJsonEditor>
                {/* Per-element style panels: right-side flyouts (like the home section editor).
                    Non-blocking so the preview stays live; the open panel's command bar button is
                    highlighted. Every one is the same flat surface the card panel uses (a
                    StyleGroupHeader per part over flat property rows, one scroll, no tabs). */}
                {/* Card/row style panel — tile / name / price / image / hover on one flat surface,
                    shared with the Home page editor. */}
                <ProductCardStylePanel
                    isOpen={openPanel === 'card'}
                    onDismiss={closePanel}
                    modeNoun={modeNoun}
                    configField='searchPageConfig'
                    card={config.card}
                    onCardChange={updateCard}
                    title={config.title}
                    onTitleChange={updateTitle}
                    price={config.price}
                    onPriceChange={updatePrice}
                    image={config.image}
                    onImageChange={updateImage}
                    cardHover={config.cardHover}
                    onCardHoverChange={updateCardHover}
                    isDark={isDark}
                />
                {/* Container panel: the box around the whole grid, plus the spacing between the
                    items inside it. ISearchContainerStyle carries no font props, so the shared rows
                    offer a few (text color, font, weight) that the container has no use for; that's
                    the deliberate trade of one shared surface (see ElementPropertyRows' own note):
                    they pass straight through and simply style nothing. */}
                <Panel isOpen={openPanel === 'container'} onDismiss={closePanel} headerText={`${modeNoun} Container`} type={PanelType.smallFixedFar} isBlocking={false} closeButtonAriaLabel='Close'>
                    <StyleGroupHeader
                        label='Container'
                        iconName={STYLE_PART_ICONS.band}
                        tooltip={`The box wrapped around every result ${modeNoun.toLowerCase()}: its background, border and inner padding.`}
                    />
                    <ElementPropertyRows value={loose(config.container)} settingKeyPrefix='storefront.searchPage.container' onChange={(patch) => updateContainer(loose(patch))} />
                    <RowGroupLabel text='Layout' iconName='GridViewMedium' />
                    <Setting id='storefront.searchPage.cardGap'>
                        <StyleRow label='Card gap'>
                            <TextField
                                type='number'
                                min={0}
                                suffix='px'
                                value={config.cardGap?.toString() ?? ''}
                                onChange={(_, v) => updateConfig({ cardGap: parseNum(v) })}
                                styles={cell.textField}
                            />
                        </StyleRow>
                    </Setting>
                </Panel>
                {/* Paginator panel — the menu box and its page buttons */}
                <Panel isOpen={openPanel === 'paginator'} onDismiss={closePanel} headerText='Paginator' type={PanelType.smallFixedFar} isBlocking={false} closeButtonAriaLabel='Close'>
                    <StylePartAccordions parts={[
                        { key: 'container', label: 'Container', iconName: STYLE_PART_ICONS.band, tooltip: 'The paginator menu box that holds the three buttons.', content: <ElementPropertyRows value={config.paginatorContainer} settingKeyPrefix='storefront.searchPage.paginatorContainer' onChange={updatePaginatorContainer} /> },
                        { key: 'cells', label: 'Cells', iconName: STYLE_PART_ICONS.base, tooltip: 'Each button inside the paginator: previous, the page range, next.', content: <ElementPropertyRows value={config.paginatorCell} settingKeyPrefix='storefront.searchPage.paginatorCell' onChange={updatePaginatorCell} /> },
                    ]} />
                </Panel>
                {/* Breadcrumbs panel — the filter chip row and each chip */}
                <Panel isOpen={openPanel === 'breadcrumb'} onDismiss={closePanel} headerText='Breadcrumbs' type={PanelType.smallFixedFar} isBlocking={false} closeButtonAriaLabel='Close'>
                    <StylePartAccordions parts={[
                        { key: 'row', label: 'Row', iconName: STYLE_PART_ICONS.band, tooltip: 'The row the selected-filter chips sit in.', content: <ElementPropertyRows value={config.breadcrumbContainer} settingKeyPrefix='storefront.searchPage.breadcrumbRow' onChange={updateBreadcrumbContainer} /> },
                        { key: 'chips', label: 'Chips', iconName: STYLE_PART_ICONS.label, tooltip: 'Each selected filter chip, e.g. "Category: Computers".', content: <ElementPropertyRows value={config.breadcrumbItem} settingKeyPrefix='storefront.searchPage.breadcrumbChip' onChange={updateBreadcrumbItem} /> },
                    ]} />
                </Panel>
                {/* Row meta chips panel — the per-row Category breadcrumb and Manufacturer
                    label, each styled as a single chip (rows view only). */}
                <Panel isOpen={openPanel === 'metaChips'} onDismiss={closePanel} headerText='Row Meta Chips' type={PanelType.smallFixedFar} isBlocking={false} closeButtonAriaLabel='Close'>
                    <StylePartAccordions parts={[
                        { key: 'category', label: 'Category', iconName: STYLE_PART_ICONS.label, tooltip: 'The whole "Category: A > B" breadcrumb on a result row, as one chip.', content: <ElementPropertyRows value={config.categoryChip} settingKeyPrefix='storefront.searchPage.categoryChip' onChange={updateCategoryChip} /> },
                        { key: 'manufacturer', label: 'Manufacturer', iconName: 'Manufacturing', tooltip: 'The whole "Manufacturer: X" label on a result row, as one chip.', content: <ElementPropertyRows value={config.manufacturerChip} settingKeyPrefix='storefront.searchPage.manufacturerChip' onChange={updateManufacturerChip} /> },
                    ]} />
                </Panel>
                {/* Filters panel: the three functional facet settings that used to sit in the
                    toolbar, plus the two style surfaces (facetPanel / facetHeading) that the
                    storefront has always rendered but nothing but Raw JSON could ever write. */}
                <Panel isOpen={openPanel === 'filters'} onDismiss={closePanel} headerText='Filters' type={PanelType.smallFixedFar} isBlocking={false} closeButtonAriaLabel='Close'>
                    <Stack tokens={{ childrenGap: 8 }} style={{ marginTop: 12 }}>
                        <Setting id='storefront.searchPage.facetsEnabled'>
                            <Toggle
                                inlineLabel
                                label='Facet filters'
                                checked={facetsEnabled}
                                onChange={(_, checked) => updateFacetSidebar({ enabled: !!checked })}
                                styles={{ root: { marginBottom: 0 } }}
                            />
                        </Setting>
                        <Setting id='storefront.searchPage.facetLayout'>
                            <ChoiceGroup
                                selectedKey={facetLayout}
                                options={FACET_LAYOUT_OPTIONS}
                                disabled={!facetsEnabled}
                                onChange={(_, option) => option && updateFacetSidebar({ layout: option.key as FacetSidebarLayout })}
                                styles={{ flexContainer: { display: 'flex', gap: '1em' } }}
                            />
                        </Setting>
                        <Setting id='storefront.searchPage.facetPriceFilter'>
                            <Toggle
                                inlineLabel
                                label='Price range'
                                disabled={!facetsEnabled}
                                checked={config.facetSidebar?.priceFilter ?? false}
                                onChange={(_, checked) => updateFacetSidebar({ priceFilter: !!checked })}
                                styles={{ root: { marginBottom: 0 } }}
                            />
                        </Setting>
                    </Stack>
                    {/* Collapsed by default: the functional toggles above are this panel's primary
                        content, and an auto-opened 13-row style group would bury them. */}
                    <StylePartAccordions startCollapsed parts={[
                        { key: 'facetPanel', label: 'Filter Panel', iconName: 'Filter', tooltip: 'The side panel the facet filters sit in (the "Side panel" layout).', content: <ElementPropertyRows value={config.facetPanel} settingKeyPrefix='storefront.searchPage.facetPanel' onChange={updateFacetPanel} /> },
                        { key: 'facetHeading', label: 'Headings', iconName: STYLE_PART_ICONS.heading, tooltip: 'Each attribute group title inside the panel, e.g. "Manufacturer" or "RAM".', content: <ElementPropertyRows value={loose(config.facetHeading)} settingKeyPrefix='storefront.searchPage.facetHeading' onChange={(patch) => updateFacetHeading(loose(patch))} /> },
                    ]} />
                </Panel>
                {/* Search Bar panel: the keyword box. Unlike the four panels above it maps to a
                    named-style target (a form input), so the picker leads and the raw rows fold
                    away underneath it. */}
                <Panel isOpen={openPanel === 'searchBar'} onDismiss={closePanel} headerText='Search Bar' type={PanelType.smallFixedFar} isBlocking={false} closeButtonAriaLabel='Close'>
                    <StyleGroupHeader
                        label='Search Bar'
                        iconName={STYLE_PART_ICONS.input}
                        tooltip='The keyword box above the results. Styling applies to the input itself, not the icon inside it.'
                    />
                    <StyleOverridesGroup
                        pickerLabel='Style'
                        rowLayout
                        target={styleTargetForConfigKey('searchPageConfig', 'searchBar')}
                        value={config.searchBar}
                        onChange={updateSearchBar}
                    >
                        <ElementPropertyRows value={config.searchBar} settingKeyPrefix='storefront.searchPage.searchBar' onChange={updateSearchBar} />
                    </StyleOverridesGroup>
                </Panel>
                <StorefrontAutocompleteStylePanel
                    context='searchPage'
                    isOpen={openPanel === 'suggestions'}
                    onDismiss={closePanel}
                    storefrontSettings={props.storefrontSettings}
                    onStorefrontSettingsChange={props.onStorefrontSettingsChange}
                />
                {/* One quick style popover for the whole preview, anchored on the row/card clicked.
                    Reads the LIVE config (not a copy captured when it opened), so picking a style
                    repaints the results underneath it and the picker shows the new value. */}
                {cardPopover && (
                    <SearchCardStylePopover
                        anchor={cardPopover.anchor}
                        modeNoun={modeNoun}
                        card={config.card}
                        onCardChange={updateCard}
                        onClearOverrides={() => {
                            // Keep the $styleRef, drop everything else: the element snaps back to the
                            // named style as designed rather than losing its style assignment too.
                            const ref = getStyleRef(config.card);
                            updateConfig({ card: ref ? { [STYLE_REF_KEY]: ref } : {} });
                        }}
                        onOpenPanel={() => setOpenPanel('card')}
                        onDismiss={() => setCardPopover(undefined)}
                    />
                )}
                </div>
            )}
            <PreviewCanvas style={{ backgroundColor }}>
                {/* Header/footer are edited only in the dedicated Header & Footer tab;
                    shown here read-only for page context. */}
                <StorefrontHeaderEditor
                    storefrontSettings={props.storefrontSettings}
                    storefrontTheme={props.storefrontTheme}
                    globalSettings={props.globalSettings}
                    globalTheme={props.globalTheme}
                    onStorefrontSettingsChange={props.onStorefrontSettingsChange}
                    editMode={false}
                    storefrontLinks={props.storefrontHeaderLinks}
                    globalLinks={props.globalHeaderLinks}
                    onLinksChange={props.onHeaderLinksChange}
                />
                <div
                    className={`storefront-body${isDark ? ' sf-search-dark' : ''}`}
                    style={{
                        maxWidth: `${props.storefrontSettings?.maxWidth ?? props.globalSettings?.maxWidth ?? 800}px`,
                        color: textColor,
                        fontFamily: resolvedStyleValue(props.storefrontSettings, 'defaultFont') || props.globalSettings.defaultFont || undefined,
                    }}
                >
                    <div className={panelLayoutOn ? 'sf-search-with-panel' : undefined}>
                        {panelLayoutOn && (
                            <FilterPanelPreview
                                priceFilter={config.facetSidebar?.priceFilter}
                                style={previewStyles.facetPanel}
                                headingStyle={facetHeadingStyle}
                                onEdit={props.editMode ? () => setOpenPanel('filters') : undefined}
                            />
                        )}
                        <div className={panelLayoutOn ? 'sf-search-main-col' : undefined}>
                            <div className='sf-search-bar'>
                                {panelLayoutOn && (
                                    <button type='button' className='sf-filter-toggle-btn' title='Show/hide filters'><Icon iconName='Filter' /></button>
                                )}
                                {/* The search bar is a real editable element: the pencil opens its STYLE
                                    panel, clicking the bar itself opens its search settings (including this
                                    page's own autocomplete config, which is independent of the header's). */}
                                <div className='sf-search-input-host sf-pencil-host'>
                                    {props.editMode && <EditPencil onClick={() => setOpenPanel('searchBar')} title='Edit search bar style' />}
                                    <StorefrontSearchBarEditor
                                        context='searchPage'
                                        storefrontSettings={props.storefrontSettings}
                                        onStorefrontSettingsChange={props.onStorefrontSettingsChange}
                                        editMode={props.editMode}
                                        isDark={isDark}
                                        onEditStyles={() => setOpenPanel('suggestions')}
                                        style={{ width: '100%', ...previewStyles.searchBar }}
                                    />
                                </div>
                                {/* Compact paginator sits inline to the right of the search bar (matches the storefront). */}
                                <PaginatorPreview containerStyle={previewStyles.paginatorContainer} cellStyle={previewStyles.paginatorCell} onEdit={props.editMode ? () => setOpenPanel('paginator') : undefined} />
                            </div>
                            {/* The panel/inline menus carry the category navigation, so breadcrumbs
                                only show when facet filters are off (mirrors the storefront). */}
                            {!facetsEnabled && (
                                <FilterBreadcrumbPreview containerStyle={previewStyles.breadcrumbContainer} itemStyle={previewStyles.breadcrumbItem} onEdit={props.editMode ? () => setOpenPanel('breadcrumb') : undefined} />
                            )}
                            {inlineLayoutOn && (
                                <InlineFiltersPreview
                                    priceFilter={config.facetSidebar?.priceFilter}
                                    onEdit={props.editMode ? () => setOpenPanel('filters') : undefined}
                                />
                            )}
                            <div className='sf-results-zone'>
                                {viewMode === 'cards' ? (
                                    <div className='sf-search-cards' style={{ ...previewStyles.container, ...(config.cardGap != null ? { gap: config.cardGap } : {}) }}>
                                        {SAMPLE_PRODUCTS.map((p) => (
                                            <SearchResultCardPreview
                                                key={p.name}
                                                product={p}
                                                styles={previewStyles}
                                                onQuickStyle={props.editMode ? (anchor) => setCardPopover({ anchor }) : undefined}
                                            />
                                        ))}
                                    </div>
                                ) : (
                                    <div className='sf-search-results' style={{ ...previewStyles.container, ...(previewStyles.container.padding != null ? {} : { paddingBottom: '2rem' }) }}>
                                        {SAMPLE_PRODUCTS.map((p) => (
                                            <SearchResultRowPreview
                                                key={p.name}
                                                product={p}
                                                styles={previewStyles}
                                                onQuickStyle={props.editMode ? (anchor) => setCardPopover({ anchor }) : undefined}
                                                onEditMeta={props.editMode ? () => setOpenPanel('metaChips') : undefined}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                <StorefrontFooterEditor
                    storefrontSettings={props.storefrontSettings}
                    storefrontTheme={props.storefrontTheme}
                    globalSettings={props.globalSettings}
                    globalTheme={props.globalTheme}
                    onStorefrontSettingsChange={props.onStorefrontSettingsChange}
                    editMode={false}
                    footerLinks={props.storefrontFooterLinks}
                    globalFooterLinks={props.globalFooterLinks}
                />
            </PreviewCanvas>
        </div>
    );
};

export default StorefrontSearchPageEditor;
